import type { FC } from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  CheckDone01,
  DotsHorizontal,
  Trash01,
} from '@untitled-ui/icons-react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import type { InAppNotification } from '../api/types'
import { useClearAllNotifications } from '../hooks/useClearAllNotifications'
import { useMarkAllNotificationsRead } from '../hooks/useMarkAllNotificationsRead'
import { useMarkNotificationRead } from '../hooks/useMarkNotificationRead'
import { useNotificationsList } from '../hooks/useNotificationsList'
import { notificationQueryKeys } from '../hooks/notificationQueryKeys'
import { groupNotificationsByDay } from '../utils/groupNotificationsByDay'
import type { PulseSnapshot } from '../utils/pulseUtils'
import { NotificationItem } from './NotificationItem'
import { paths } from '@/paths'
import {
  SIDE_NAV_POPOVER_OFFSET_PX,
  sideNavPopoverBackdropSx,
  sideNavPopoverPaperShadow,
} from '@/layouts/dashboard/vertical-layout/side-nav-layout-dimensions'
import { densityRem } from '@/theme/base/density'
import useTranslate from '@/hooks/use-translate'

const PANEL_WIDTH_PX = 440

type NotificationPanelProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  headId: string | null
  unreadCount: number
  onOpenChangelog: () => void
}

export const NotificationPanel: FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
  headId,
  unreadCount,
  onOpenChangelog,
}) => {
  const { t } = useTranslate('notifications')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const clearAll = useClearAllNotifications()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useNotificationsList(open, headId)

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  )
  const dayGroups = useMemo(
    () => groupNotificationsByDay(notifications),
    [notifications]
  )

  useEffect(() => {
    if (!open || notifications.length === 0) {
      return
    }
    const visibleUnread = notifications.filter(
      (notification) => notification.readAt == null
    ).length
    if (visibleUnread === 0) {
      return
    }

    queryClient.setQueryData<PulseSnapshot>(
      notificationQueryKeys.pulse(),
      (current) => {
        if ((current?.unreadCount ?? 0) >= visibleUnread) {
          return current
        }
        return {
          unreadCount: visibleUnread,
          headId: current?.headId ?? headId,
        }
      }
    )
  }, [open, notifications, queryClient, headId])

  const handleNotificationClick = (notification: InAppNotification) => {
    if (!notification.readAt) {
      markRead.mutate(notification.id)
    }

    onClose()

    if (notification.type === 'app_update') {
      onOpenChangelog()
      return
    }

    if (notification.batchImportId) {
      navigate(paths.dashboard.documents.batchImport(notification.batchImportId))
    }
  }

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      hideBackdrop={false}
      BackdropProps={{ sx: sideNavPopoverBackdropSx }}
      marginThreshold={12}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            boxShadow: sideNavPopoverPaperShadow,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'min(640px, calc(100vh - 24px))',
            ml: `${SIDE_NAV_POPOVER_OFFSET_PX}px`,
            overflow: 'hidden',
            width: { xs: 'calc(100vw - 16px)', sm: PANEL_WIDTH_PX },
          },
        },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {t('title')}
          </Typography>
          {unreadCount > 0 && (
            <Box
              sx={{
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
                borderRadius: 999,
                color: 'error.main',
                flexShrink: 0,
                fontSize: densityRem(0.75),
                fontWeight: 600,
                lineHeight: 1.2,
                px: 1,
                py: 0.375,
                whiteSpace: 'nowrap',
              }}
            >
              {t('unreadCount', { count: unreadCount })}
            </Box>
          )}
        </Box>
        {notifications.length > 0 && (
          <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 0.25 }}>
            <Tooltip title={t('markAllRead')}>
              <span>
                <IconButton
                  aria-label={t('markAllRead')}
                  disabled={unreadCount === 0 || markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <SvgIcon fontSize="small">
                    <CheckDone01 />
                  </SvgIcon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('moreActions')}>
              <IconButton
                aria-label={t('moreActions')}
                aria-haspopup="menu"
                onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <SvgIcon fontSize="small">
                  <DotsHorizontal />
                </SvgIcon>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                disabled={clearAll.isPending}
                onClick={() => {
                  setMenuAnchorEl(null)
                  clearAll.mutate()
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 'unset !important' }}>
                  <SvgIcon fontSize="small">
                    <Trash01 />
                  </SvgIcon>
                </ListItemIcon>
                {t('clearAll')}
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              px: 1.5,
              py: 4,
              textAlign: 'center',
            }}
          >
            {t('empty')}
          </Typography>
        ) : (
          dayGroups.map((group) => (
            <Fragment key={group.key}>
              <Typography
                component="h3"
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  fontSize: densityRem(0.6875),
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  lineHeight: 1.5,
                  pb: 0.5,
                  pt: 1.25,
                  px: 2,
                }}
              >
                {t(`group_${group.key}`)}
              </Typography>
              {group.notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onClick={handleNotificationClick}
                  />
                  {index < group.notifications.length - 1 && (
                    <Divider sx={{ borderColor: 'divider' }} />
                  )}
                </Box>
              ))}
            </Fragment>
          ))
        )}
        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <ButtonBase
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              sx={{
                borderRadius: 1,
                color: 'primary.main',
                fontSize: densityRem(0.875),
                fontWeight: 600,
                px: 1.5,
                py: 0.75,
              }}
            >
              {isFetchingNextPage ? (
                <CircularProgress size={16} />
              ) : (
                t('loadMore')
              )}
            </ButtonBase>
          </Box>
        )}
      </Box>
    </Popover>
  )
}
